import { ViewMode } from '../domain/types';
import { ActiveToolType } from '../features/modals/ToolDrawerModal';

export interface ShortcutMods {
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
  key: string;
  typing: boolean;
}

export interface ModalFlags {
  searchTarget: string | null;
  activeTool: ActiveToolType;
  isMergeModalOpen: boolean;
  isDailyConfirmOpen: boolean;
  isDrawerOpen: boolean;
}

export type ShortcutId =
  | 'search'
  | 'close'
  | 'radar'
  | 'jira'
  | 'slack'
  | 'github'
  | 'gmail'
  | 'agents'
  | 'daily'
  | 'reminders'
  | 'notes'
  | 'sidebar';

export type ShortcutModifier =
  | 'none'
  | 'primary'
  | 'alt'
  | 'shift'
  | 'primary-shift'
  | 'alt-shift'
  | 'primary-alt'
  | 'primary-alt-shift';

export interface ShortcutBinding {
  key: string;
  modifier: ShortcutModifier;
}

export type ShortcutBindings = Record<ShortcutId, ShortcutBinding>;

export interface ShortcutDefinition {
  id: ShortcutId;
  label: string;
  description: string;
  category: 'Navigation' | 'Workspace' | 'Interface';
}

export const SHORTCUT_DEFINITIONS: readonly ShortcutDefinition[] = [
  {
    id: 'search',
    label: 'Abrir búsqueda',
    description: 'Abre la búsqueda global',
    category: 'Interface',
  },
  {
    id: 'close',
    label: 'Cerrar ventanas',
    description: 'Cierra el modal o cajón activo',
    category: 'Interface',
  },
  { id: 'radar', label: 'Radar', description: 'Ir al Radar de Atención', category: 'Navigation' },
  { id: 'jira', label: 'Tareas', description: 'Ir a Tareas (Jira)', category: 'Navigation' },
  { id: 'slack', label: 'Mensajes', description: 'Ir a Mensajes (Slack)', category: 'Navigation' },
  { id: 'github', label: 'Código', description: 'Ir a Código (GitHub)', category: 'Navigation' },
  { id: 'gmail', label: 'Correo', description: 'Ir a Correo (Gmail)', category: 'Navigation' },
  { id: 'agents', label: 'Agentes', description: 'Ir a Agentes Autónomos', category: 'Navigation' },
  {
    id: 'daily',
    label: 'Daily',
    description: 'Abrir el asistente de Daily Standup',
    category: 'Workspace',
  },
  {
    id: 'reminders',
    label: 'Recordatorios',
    description: 'Abrir Recordatorios',
    category: 'Workspace',
  },
  { id: 'notes', label: 'Notas', description: 'Abrir Notas rápidas', category: 'Workspace' },
  {
    id: 'sidebar',
    label: 'Barra lateral',
    description: 'Mostrar u ocultar la barra lateral',
    category: 'Interface',
  },
];

export const DEFAULT_SHORTCUT_BINDINGS: ShortcutBindings = {
  search: { key: 'k', modifier: 'primary' },
  close: { key: 'Escape', modifier: 'none' },
  radar: { key: '1', modifier: 'none' },
  jira: { key: '2', modifier: 'none' },
  slack: { key: '3', modifier: 'none' },
  github: { key: '4', modifier: 'none' },
  agents: { key: '5', modifier: 'none' },
  gmail: { key: '7', modifier: 'none' },
  daily: { key: 'd', modifier: 'primary' },
  reminders: { key: 'r', modifier: 'primary' },
  notes: { key: 'n', modifier: 'primary' },
  sidebar: { key: 'b', modifier: 'primary' },
};

export type ShortcutAction =
  | { kind: 'open-search' }
  | { kind: 'close-modals' }
  | { kind: 'navigate'; view: ViewMode }
  | { kind: 'open-tool'; tool: NonNullable<ActiveToolType> }
  | { kind: 'toggle-sidebar' };

const NAV_SHORTCUTS: Array<{ id: ShortcutId; view: ViewMode }> = [
  { id: 'radar', view: 'radar' },
  { id: 'jira', view: 'jira' },
  { id: 'slack', view: 'slack' },
  { id: 'github', view: 'github' },
  { id: 'agents', view: 'agents' },
  { id: 'gmail', view: 'gmail' },
];

const TOOL_SHORTCUTS: Array<{ id: ShortcutId; tool: NonNullable<ActiveToolType> }> = [
  { id: 'daily', tool: 'daily' },
  { id: 'reminders', tool: 'notes' },
  { id: 'notes', tool: 'notes' },
];

function normalizedKey(key: string): string {
  return key.length === 1 ? key.toLowerCase() : key;
}

function sameBinding(left: ShortcutBinding, right: ShortcutBinding): boolean {
  return normalizedKey(left.key) === normalizedKey(right.key) && left.modifier === right.modifier;
}

export function isFormControlTag(tagName: string): boolean {
  if (tagName === 'input') return true;
  if (tagName === 'textarea') return true;
  return tagName === 'select';
}

export function isEditableElement(el: HTMLElement): boolean {
  if (el.isContentEditable) return true;
  if (el.contentEditable === 'true') return true;
  const attr = el.getAttribute('contenteditable');
  if (attr === 'true') return true;
  return attr === '';
}

/** Checks if the event target is currently an active text input or editable field. */
export function isTypingInInput(target: EventTarget | null): boolean {
  if (!target) return false;
  if (!(target instanceof HTMLElement)) return false;
  if (isFormControlTag(target.tagName.toLowerCase())) return true;
  return isEditableElement(target);
}

function hasSearchOrToolOpen(flags: ModalFlags): boolean {
  if (flags.searchTarget !== null) return true;
  return flags.activeTool !== null;
}

function hasDialogOpen(flags: ModalFlags): boolean {
  if (flags.isMergeModalOpen) return true;
  if (flags.isDailyConfirmOpen) return true;
  return flags.isDrawerOpen;
}

export function hasOpenModal(flags: ModalFlags): boolean {
  if (hasSearchOrToolOpen(flags)) return true;
  return hasDialogOpen(flags);
}

export function hasPrimaryModifier(mods: ShortcutMods): boolean {
  return mods.metaKey || mods.ctrlKey;
}

export function hasAnyModifier(mods: ShortcutMods): boolean {
  if (hasPrimaryModifier(mods)) return true;
  if (mods.altKey) return true;
  return mods.shiftKey;
}

export function isPlainModifier(mods: ShortcutMods): boolean {
  if (mods.altKey || mods.shiftKey) return false;
  return hasPrimaryModifier(mods);
}

export function matchesDigitNav(mods: ShortcutMods, digit: string, modalOpen: boolean): boolean {
  if (mods.key !== digit) return false;
  if (isPlainModifier(mods)) return true;
  if (hasAnyModifier(mods)) return false;
  return !mods.typing && !modalOpen;
}

const MODIFIER_MATCHERS: Record<ShortcutModifier, (mods: ShortcutMods) => boolean> = {
  none: (mods) => !hasPrimaryModifier(mods) && !mods.altKey && !mods.shiftKey,
  primary: (mods) => hasPrimaryModifier(mods) && !mods.altKey && !mods.shiftKey,
  alt: (mods) => mods.altKey && !hasPrimaryModifier(mods) && !mods.shiftKey,
  shift: (mods) => mods.shiftKey && !hasPrimaryModifier(mods) && !mods.altKey,
  'primary-shift': (mods) => hasPrimaryModifier(mods) && mods.shiftKey && !mods.altKey,
  'alt-shift': (mods) => mods.altKey && mods.shiftKey && !hasPrimaryModifier(mods),
  'primary-alt': (mods) => hasPrimaryModifier(mods) && mods.altKey && !mods.shiftKey,
  'primary-alt-shift': (mods) => hasPrimaryModifier(mods) && mods.altKey && mods.shiftKey,
};

function modifierMatches(mods: ShortcutMods, modifier: ShortcutModifier): boolean {
  return MODIFIER_MATCHERS[modifier](mods);
}

function matchesConfiguredBinding(
  id: ShortcutId,
  mods: ShortcutMods,
  bindings: ShortcutBindings,
): boolean {
  const binding = bindings[id];
  if (!binding || normalizedKey(mods.key) !== normalizedKey(binding.key)) return false;
  if (modifierMatches(mods, binding.modifier)) return true;
  if (!sameBinding(binding, DEFAULT_SHORTCUT_BINDINGS[id])) return false;
  if (id === 'radar' || id === 'jira' || id === 'slack' || id === 'github' || id === 'agents') {
    return hasPrimaryModifier(mods);
  }
  if (id === 'daily' || id === 'reminders' || id === 'notes') {
    return mods.altKey && !mods.shiftKey && !hasPrimaryModifier(mods);
  }
  return id === 'sidebar' && hasPrimaryModifier(mods);
}

export function resolveSearchAction(
  mods: ShortcutMods,
  bindings: ShortcutBindings = DEFAULT_SHORTCUT_BINDINGS,
): ShortcutAction | null {
  if (!matchesConfiguredBinding('search', mods, bindings)) return null;
  return { kind: 'open-search' };
}

export function resolveEscapeAction(
  mods: ShortcutMods,
  modalOpen: boolean,
  bindings: ShortcutBindings = DEFAULT_SHORTCUT_BINDINGS,
): ShortcutAction | null {
  if (!matchesConfiguredBinding('close', mods, bindings)) return null;
  if (!modalOpen) return null;
  return { kind: 'close-modals' };
}

export function resolveNavAction(
  mods: ShortcutMods,
  modalOpen: boolean,
  bindings: ShortcutBindings = DEFAULT_SHORTCUT_BINDINGS,
): ShortcutAction | null {
  for (const shortcut of NAV_SHORTCUTS) {
    if (!matchesConfiguredBinding(shortcut.id, mods, bindings)) continue;
    if (
      bindings[shortcut.id].modifier === 'none' &&
      !hasPrimaryModifier(mods) &&
      (mods.typing || modalOpen)
    ) {
      return null;
    }
    return { kind: 'navigate', view: shortcut.view };
  }
  return null;
}

export function resolveToolAction(
  mods: ShortcutMods,
  bindings: ShortcutBindings = DEFAULT_SHORTCUT_BINDINGS,
): ShortcutAction | null {
  for (const shortcut of TOOL_SHORTCUTS) {
    if (matchesConfiguredBinding(shortcut.id, mods, bindings)) {
      return { kind: 'open-tool', tool: shortcut.tool };
    }
  }
  return null;
}

export function resolveSidebarAction(
  mods: ShortcutMods,
  modalOpen: boolean,
  bindings: ShortcutBindings = DEFAULT_SHORTCUT_BINDINGS,
): ShortcutAction | null {
  if (matchesConfiguredBinding('sidebar', mods, bindings)) {
    if (bindings.sidebar.modifier === 'none' && (mods.typing || modalOpen)) return null;
    return { kind: 'toggle-sidebar' };
  }
  if (
    sameBinding(bindings.sidebar, DEFAULT_SHORTCUT_BINDINGS.sidebar) &&
    !hasAnyModifier(mods) &&
    mods.key === '['
  ) {
    return !mods.typing && !modalOpen ? { kind: 'toggle-sidebar' } : null;
  }
  return null;
}

function firstAction(candidates: Array<ShortcutAction | null>): ShortcutAction | null {
  for (const action of candidates) {
    if (action) return action;
  }
  return null;
}

function resolveEnabledShortcut(
  mods: ShortcutMods,
  flags: ModalFlags,
  bindings: ShortcutBindings,
): ShortcutAction | null {
  const modalOpen = hasOpenModal(flags);
  return firstAction([
    resolveSearchAction(mods, bindings),
    resolveEscapeAction(mods, modalOpen, bindings),
    resolveNavAction(mods, modalOpen, bindings),
    resolveToolAction(mods, bindings),
    resolveSidebarAction(mods, modalOpen, bindings),
  ]);
}

/** Pure resolver: keyboard state + modal flags + bindings -> at most one action. */
export function resolveShortcut(
  mods: ShortcutMods,
  flags: ModalFlags,
  enabled: boolean,
  bindings: ShortcutBindings = DEFAULT_SHORTCUT_BINDINGS,
): ShortcutAction | null {
  if (!enabled) return null;
  return resolveEnabledShortcut(mods, flags, bindings);
}

export function modsFromKeyboardEvent(e: KeyboardEvent, typing: boolean): ShortcutMods {
  return {
    metaKey: e.metaKey,
    ctrlKey: e.ctrlKey,
    altKey: e.altKey,
    shiftKey: e.shiftKey,
    key: e.key,
    typing,
  };
}
